import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, CalendarClock, Users, Store, CreditCard, Share2, Sparkles } from "lucide-react";

const CREAM = "#FBF6EC";
const INK = "#141210";
const CORAL = "#FF5A36";
const GREEN = "#12715A";
const SUN = "#F4B740";

export default function Landing() {
  return (
    <div className="font-sans" style={{ background: CREAM, color: INK }}>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#FFF1E6 0%,#FFE1D6 40%,#FDEBC8 70%,#E4F2EA 100%)" }}>
        {/* graphic shapes, no photos */}
        <div className="anim-float-slow pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full" style={{ background: SUN, opacity: 0.55, filter: "blur(6px)" }} />
        <div className="anim-float pointer-events-none absolute top-40 -left-28 w-72 h-72 rounded-full" style={{ background: CORAL, opacity: 0.30 }} />
        <div className="anim-float-rot pointer-events-none absolute bottom-4 right-1/3 w-44 h-44 rounded-3xl" style={{ background: GREEN, opacity: 0.22 }} />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold" style={{ background: "#fff", color: CORAL, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
              <Sparkles className="w-4 h-4" /> Prenotazioni con caparra
            </span>
            <h1 className="font-display font-extrabold tracking-tight mt-6 mb-6 leading-[0.98] text-5xl sm:text-6xl lg:text-7xl">
              Gruppi grandi,<br /><span style={{ background: "linear-gradient(90deg,#FF5A36,#F4B740)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>zero</span> no-show.
            </h1>
            <p className="text-lg sm:text-xl mb-9 max-w-xl font-medium" style={{ color: "#4a453f" }}>
              Accontami raccoglie le richieste di prenotazione e incassa la caparra sul <b>tuo</b> conto. Semplice, chiaro, professionale.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/registra" data-testid="hero-register-btn"
                className="px-7 py-4 rounded-full font-bold text-white flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                style={{ background: CORAL, boxShadow: "0 12px 30px rgba(255,90,54,0.35)" }}>
                Registra il tuo ristorante <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/login" data-testid="hero-login-btn"
                className="px-7 py-4 rounded-full font-bold flex items-center gap-2 transition-colors"
                style={{ background: "#fff", color: INK, border: `2px solid ${INK}` }}>
                <Store className="w-5 h-5" /> Area ristoratori
              </Link>
            </div>
          </motion.div>

          {/* Graphic booking card mockup (no photo) */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <div className="anim-float rounded-3xl p-6 bg-white" style={{ boxShadow: "0 30px 60px rgba(20,18,16,0.14)" }}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 font-extrabold text-lg"><span style={{ color: CORAL }}>●</span> Nuova richiesta</div>
                <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: "#FEE9E2", color: CORAL }}>Da confermare</span>
              </div>
              {[["Persone", "10 ospiti"], ["Data", "Sab 20 giu · 20:30"], ["Caparra", "€ 150,00"]].map(([k, v], i) => (
                <div key={k} className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: "#efe9dc" }}>
                  <span className="font-semibold" style={{ color: "#8b857b" }}>{k}</span>
                  <span className="font-extrabold" style={{ color: i === 2 ? GREEN : INK }}>{v}</span>
                </div>
              ))}
              <button className="mt-5 w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2" style={{ background: GREEN }}>
                <CreditCard className="w-4 h-4" /> Invia link di pagamento
              </button>
            </div>
            <div className="anim-float-slow absolute -bottom-5 -left-5 rounded-2xl px-4 py-3 bg-white font-bold flex items-center gap-2" style={{ boxShadow: "0 14px 30px rgba(20,18,16,0.12)" }}>
              <ShieldCheck className="w-5 h-5" style={{ color: GREEN }} /> Caparra incassata ✓
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURE TILES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
        {[
          { icon: ShieldCheck, t: "Caparra sicura", d: "I soldi arrivano direttamente sul conto del ristorante.", c: CORAL, bg: "#FEEDE7" },
          { icon: CalendarClock, t: "Orari e fasce", d: "Apertura e slot da 30 minuti, sempre aggiornati.", c: GREEN, bg: "#E3F1EB" },
          { icon: Users, t: "Solo gruppi grandi", d: "Richieste online per tavoli numerosi, meno caos.", c: "#B4741A", bg: "#FBF0D9" },
        ].map((f, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="rounded-3xl p-7 bg-white" style={{ boxShadow: "0 10px 30px rgba(20,18,16,0.06)" }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: f.bg }}>
              <f.icon className="w-6 h-6" style={{ color: f.c }} />
            </div>
            <h3 className="font-display font-extrabold text-xl mb-1.5">{f.t}</h3>
            <p className="font-medium" style={{ color: "#6b665d" }}>{f.d}</p>
          </motion.div>
        ))}
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl mb-3">Come funziona</h2>
        <p className="text-lg font-medium mb-12" style={{ color: "#6b665d" }}>Attivo in pochi minuti, con il tuo link privato.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { n: "1", icon: Store, t: "Registra e personalizza", d: "Logo, orari, caparra e coperti.", c: CORAL },
            { n: "2", icon: CreditCard, t: "Collega il tuo Stripe", d: "Le caparre arrivano sul tuo conto.", c: GREEN },
            { n: "3", icon: Share2, t: "Condividi il link", d: "Sito, social, WhatsApp: solo il tuo locale.", c: SUN },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="relative rounded-3xl p-8 overflow-hidden" style={{ background: INK, color: CREAM }}>
              <span className="absolute top-4 right-6 font-display font-black text-7xl" style={{ color: "rgba(255,255,255,0.06)" }}>{s.n}</span>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: s.c }}>
                <s.icon className="w-6 h-6" style={{ color: INK }} />
              </div>
              <h3 className="font-display font-extrabold text-2xl mb-2">{s.t}</h3>
              <p className="font-medium" style={{ color: "rgba(251,246,236,0.7)" }}>{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-[2rem] p-10 sm:p-16 text-center relative overflow-hidden" style={{ background: CORAL }}>
          <div className="pointer-events-none absolute -top-16 -left-10 w-56 h-56 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
          <div className="pointer-events-none absolute -bottom-20 -right-6 w-64 h-64 rounded-full" style={{ background: "rgba(0,0,0,0.08)" }} />
          <div className="relative">
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-white mb-4">Pronto a ridurre i no-show?</h2>
            <p className="text-white/90 font-medium mb-8 max-w-xl mx-auto text-lg">Attiva le prenotazioni con caparra per il tuo ristorante in pochi minuti.</p>
            <Link to="/registra" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold bg-white" style={{ color: CORAL }}>
              Registra il tuo ristorante <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 text-center text-sm font-medium" style={{ color: "#8b857b" }}>
        Accontami · Prenotazioni con caparra online
      </footer>
    </div>
  );
}
